import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import Flatpickr from 'react-flatpickr';
import "flatpickr/dist/flatpickr.min.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const Dashboard = () => {
    const [stats, setStats] = useState({
        totalEvents: 0,
        totalOrganizers: 0,
        totalUsers: 0,
        totalRevenue: 0,
        totalTicketsSold: 0,
        activeEvents: 0,
        pendingPayouts: 0,
        totalPayoutAmount: 0
    });
    const [recentEvents, setRecentEvents] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [categoryData, setCategoryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState([]);

    const handlePrintReport = () => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            window.print();
        }, 300);
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (dateRange.length === 2 || dateRange.length === 0) {
            fetchDashboardData();
        }
    }, [dateRange]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const params = {};
            if (dateRange.length === 2) {
                params.start_date = dateRange[0].toISOString().split('T')[0];
                params.end_date = dateRange[1].toISOString().split('T')[0];
            }

            const statsResponse = await api.get('/admin/dashboard/stats', { params });
            const dashboardData = statsResponse.data.data;

            setStats({
                totalEvents: dashboardData.stats.totalEvents,
                totalOrganizers: dashboardData.stats.totalOrganizers,
                totalUsers: dashboardData.stats.totalUsers,
                totalRevenue: dashboardData.stats.totalRevenue,
                totalTicketsSold: dashboardData.stats.totalTicketsSold,
                activeEvents: dashboardData.stats.activeEvents,
                totalOrders: dashboardData.stats.totalOrders,
                pendingPayouts: dashboardData.stats.pendingPayouts || 0,
                totalPayoutAmount: dashboardData.stats.totalPayoutAmount || 0,
                activeOrganizers: dashboardData.organizers.active|| 0
            });

            const monthlyData = dashboardData.revenue.monthly || [];
            const months = monthlyData.map(item => {
                const date = new Date(item.month + '-01');
                return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            });
            const revenues = monthlyData.map(item => parseFloat(item.revenue || 0));

            setChartData({
                labels: months.length > 0 ? months : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                datasets: [{
                    label: 'Revenue',
                    data: revenues.length > 0 ? revenues : [0, 0, 0, 0, 0, 0],
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    pointBackgroundColor: '#3b82f6',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    borderWidth: 3,
                    tension: 0.4,
                    fill: true,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                }]
            });

            const categoryStats = dashboardData.eventsByCategory || [];
            setCategoryData({
                labels: categoryStats.map(item => item.category || 'Uncategorized'),
                datasets: [{
                    data: categoryStats.map(item => item.count || 0),
                    backgroundColor: [
                        '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff'
                    ],
                    hoverBackgroundColor: [
                        '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'
                    ],
                    borderWidth: 2,
                    borderColor: '#ffffff',
                }]
            });

            const eventsResponse = await api.get('/admin/events');
            setRecentEvents(eventsResponse.data.data?.slice(0, 5) || []);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
                    <p className="text-gray-600 mt-1">Welcome back!</p>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    {/* Date Range Picker */}
                    <div className="relative">
                        <div className="relative">
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    maxDate: 'today',
                                }}
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                                className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all w-full sm:w-64"
                                placeholder="Select date range"
                            />
                            <i className="fas fa-calendar-alt text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                            {dateRange?.length > 0 && (
                                <button
                                    onClick={() => setDateRange([])}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Clear date range"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Generate Report Button */}
                    <button
                        onClick={handlePrintReport}
                        className="no-print inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
                    >
                        <i className="fas fa-download mr-2"></i>
                        Generate Report
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Events */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Events</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalEvents.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                            <i className="fas fa-calendar text-blue-600 text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="flex items-center text-sm text-gray-600">
                            <span className="flex-1">Active Events</span>
                            <span className="font-medium">{stats.activeEvents}</span>
                        </div>
                    </div>
                </div>

                {/* Total Revenue */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">
                                QAR {parseFloat(stats.totalRevenue).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                            </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                            <i className="fas fa-chart-line text-green-600 text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="flex items-center text-sm text-gray-600">
                            <span className="flex-1">Avg per event</span>
                            <span className="font-medium">
                QAR {stats.totalEvents > 0 ? (stats.totalRevenue / stats.totalEvents).toLocaleString('en-US', { minimumFractionDigits: 0 }) : 0}
              </span>
                        </div>
                    </div>
                </div>

                {/* Tickets Sold */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Tickets Sold</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalTicketsSold.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                            <i className="fas fa-ticket-alt text-purple-600 text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="flex items-center text-sm text-gray-600">
                            <span className="flex-1">Conversion rate</span>
                            <span className="font-medium">
                {stats.totalUsers > 0 ? ((stats.totalTicketsSold / stats.totalUsers) * 100).toFixed(1) : 0}%
              </span>
                        </div>
                    </div>
                </div>

                {/* Pending Payouts */}
                <Link to="/admin/payouts" className="block">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow hover:border-blue-300">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Pending Payouts</p>
                                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.pendingPayouts}</p>
                            </div>
                            <div className="p-3 bg-yellow-50 rounded-lg">
                                <i className="fas fa-money-check-alt text-yellow-600 text-xl"></i>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="flex items-center text-sm text-gray-600">
                                <span className="flex-1">Total amount</span>
                                <span className="font-medium">
                  QAR {parseFloat(stats.totalPayoutAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                </span>
                            </div>
                        </div>
                    </div>
                </Link>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Chart */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Revenue Overview</h3>
                            <p className="text-sm text-gray-600">Monthly revenue performance</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                                <span className="text-sm text-gray-600">Revenue</span>
                            </div>
                        </div>
                    </div>
                    <div className="h-80">
                        {chartData && (
                            <Line
                                data={chartData}
                                options={{
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            backgroundColor: 'rgba(0,0,0,0.9)',
                                            padding: 12,
                                            titleColor: '#fff',
                                            bodyColor: '#fff',
                                            borderColor: '#3b82f6',
                                            borderWidth: 1,
                                            callbacks: {
                                                label: function(context) {
                                                    return `Revenue: QAR ${context.parsed.y.toLocaleString()}`;
                                                }
                                            }
                                        }
                                    },
                                    scales: {
                                        x: {
                                            grid: { display: false },
                                            ticks: { color: '#6b7280' }
                                        },
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: '#f3f4f6' },
                                            ticks: {
                                                color: '#6b7280',
                                                callback: function(value) {
                                                    return 'QAR ' + value.toLocaleString();
                                                }
                                            }
                                        }
                                    },
                                    interaction: {
                                        intersect: false,
                                        mode: 'index'
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Category Distribution */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Category Distribution</h3>
                        <p className="text-sm text-gray-600">Events by category</p>
                    </div>
                    <div className="h-64">
                        {categoryData && (
                            <Pie
                                data={categoryData}
                                options={{
                                    maintainAspectRatio: false,
                                    responsive: true,
                                    cutout: '65%',
                                    plugins: {
                                        legend: {
                                            position: 'bottom',
                                            labels: {
                                                padding: 20,
                                                font: { size: 11 },
                                                usePointStyle: true,
                                                pointStyle: 'circle'
                                            }
                                        },
                                        tooltip: {
                                            backgroundColor: 'rgba(0,0,0,0.9)',
                                            padding: 12,
                                            titleColor: '#fff',
                                            bodyColor: '#fff',
                                            callbacks: {
                                                label: function(context) {
                                                    const label = context.label || '';
                                                    const value = context.parsed || 0;
                                                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                    return `${label}: ${value} (${percentage}%)`;
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom Section */}
            {/* Bottom Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Events - Takes 2/3 width */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 no-print">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Recent Events</h3>
                                <p className="text-sm text-gray-600">Latest events created on the platform</p>
                            </div>
                            <Link
                                to="/admin/events"
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                                View All
                                <i className="fas fa-arrow-right ml-2"></i>
                            </Link>
                        </div>
                    </div>
                    <div className="px-2 pb-2">
                        {recentEvents.length === 0 ? (
                            <div className="text-center py-8">
                                <i className="fas fa-calendar-times text-4xl text-gray-300 mb-3"></i>
                                <p className="text-gray-500">No events found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                    <tr className="bg-gray-50">
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                            Event
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {recentEvents.map((event) => (
                                        <tr key={event.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    {event.images && event.images[0] ? (
                                                        <img
                                                            src={event.images[0].image_url}
                                                            alt={event.title}
                                                            className="w-10 h-10 rounded-lg object-cover mr-3"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mr-3">
                                                            <i className="fas fa-calendar text-blue-600 text-sm"></i>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">{event.title}</div>
                                                        <div className="text-xs text-gray-600">{event.location || 'N/A'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{new Date(event.event_start).toLocaleDateString()}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        event.status
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                    }`}>
                      {event.status ? 'Active' : 'Inactive'}
                    </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <Link
                                                    to={`/events/${event.id}`}
                                                    className="text-blue-600 hover:text-blue-900 transition-colors inline-flex items-center"
                                                    title="View Event"
                                                >
                                                    <i className="fas fa-external-link-alt mr-1"></i>
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Platform Overview & Quick Actions - Takes 1/3 width */}
                <div className="lg:col-span-1 flex flex-col gap-6">
                    {/* Platform Overview */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-6">Platform Overview</h3>
                        <div className="space-y-8 pb-12">
                            {/* Organizers */}
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">Organizers</span>
                                    <span className="text-xl font-bold text-blue-600">{stats.totalOrganizers}</span>
                                </div>
                                <div className="text-xs text-gray-600">Active event creators</div>
                            </div>

                            {/* Users */}
                            <div className="p-4 bg-green-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">Total Users</span>
                                    <span className="text-xl font-bold text-green-600">{stats.totalUsers}</span>
                                </div>
                                <div className="text-xs text-gray-600">Registered platform users</div>
                            </div>

                            {/* Ticket Sales */}
                            <div className="p-4 bg-purple-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">Ticket Sales</span>
                                    <span className="text-xl font-bold text-purple-600">{stats.totalTicketsSold}</span>
                                </div>
                                <div className="text-xs text-gray-600">
                                    Avg: {stats.totalEvents > 0 ? (stats.totalTicketsSold / stats.totalEvents).toFixed(1) : 0} per event
                                </div>
                            </div>

                            {/* Events */}
                            <div className="p-4 bg-yellow-50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">Active Events</span>
                                    <span className="text-xl font-bold text-yellow-600">{stats.activeEvents}</span>
                                </div>
                                <div className="text-xs text-gray-600">Out of {stats.totalEvents} total</div>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
        </div>
    );
};

export default Dashboard;